import React, { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Form } from "@/components/ui/form";
import { toast } from 'sonner';
import { CardDetails, VerificationResult } from '@/utils/aiVerificationService';
import { verifyCardWithAI } from '@/utils/aiVerificationService';
import { processStripePayment, PaymentStatus } from '@/utils/stripeService';
import AiCardVerification from './AiCardVerification';
import CardFormFields from './CardFormFields';
import SaveCardCheckbox from './SaveCardCheckbox';
import CardActionButtons from './CardActionButtons';
import { VerificationStatus } from '@/types/payment';

export const debitCardSchema = z.object({
  cardNumber: z.string().regex(/^\d{16}$/, "Card number must be 16 digits"),
  cardholderName: z.string().min(3, "Name must be at least 3 characters"),
  expiryDate: z.string().regex(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, "Expiry date must be in MM/YY format"),
  cvv: z.string().regex(/^\d{3,4}$/, "CVV must be 3 or 4 digits"),
  saveCard: z.boolean().default(true),
});

export type CardFormValues = CardDetails & { saveCard: boolean };

interface CardPaymentFormProps {
  form: ReturnType<typeof useForm<CardFormValues>>;
  onAiVerified?: (success: boolean) => void;
  onPaymentProcessed?: (success: boolean, transactionId?: string) => void;
  recipientId?: string;
  amount?: number;
  disableProcessing?: boolean;
}

const CardPaymentForm: React.FC<CardPaymentFormProps> = ({ 
  form, 
  onAiVerified,
  onPaymentProcessed,
  recipientId = '',
  amount = 0,
  disableProcessing = false
}) => {
  const [aiVerificationStatus, setAiVerificationStatus] = useState<VerificationStatus>('idle');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');
  const [aiScore, setAiScore] = useState<number | null>(null);
  const [riskLevel, setRiskLevel] = useState<'low' | 'medium' | 'high' | null>(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeData, setStripeData] = useState<any>(null);

  const handleVerifyCard = async () => {
    // Validate form before AI verification
    const valid = await form.trigger();
    if (!valid) return;
    
    const values = form.getValues();
    
    try {
      setAiVerificationStatus('verifying');
      
      const result = await verifyCardWithAI({
        cardNumber: values.cardNumber,
        cardholderName: values.cardholderName,
        expiryDate: values.expiryDate,
        cvv: values.cvv
      });
      
      setAiScore(result.score || null);
      setRiskLevel(result.riskLevel || null);
      setStripeReady(result.stripeReady || false);
      setStripeData(result.stripeResponse || null);
      
      if (result.success) {
        setAiVerificationStatus('verified');
        toast.success('Card verified with Stripe', {
          description: `Risk level: ${result.riskLevel}, Score: ${result.score}/100`
        });
        
        if (onAiVerified) onAiVerified(true);
      } else {
        setAiVerificationStatus('failed');
        toast.error('Card verification failed', {
          description: result.message
        });
        
        if (onAiVerified) onAiVerified(false);
      }
    } catch (error) {
      console.error('AI verification error:', error);
      setAiVerificationStatus('failed');
      toast.error('Verification error', {
        description: 'Could not verify card at this time'
      });
      
      if (onAiVerified) onAiVerified(false);
    }
  };
  
  const handleProcessPayment = async () => {
    if (disableProcessing || !recipientId || !amount || !stripeReady) return;
    
    const values = form.getValues();
    
    try {
      setPaymentStatus('processing');
      
      // Pass the card details and recipient ID to processStripePayment
      const cardDetails = {
        cardNumber: values.cardNumber,
        cardholderName: values.cardholderName,
        expiryDate: values.expiryDate,
        cvv: values.cvv
      };
      
      const result = await processStripePayment(cardDetails, recipientId);
      
      if (result.success) {
        setPaymentStatus('succeeded');
        toast.success('Payment successful', {
          description: `Transaction ID: ${result.transactionId}`
        });
        
        if (onPaymentProcessed) onPaymentProcessed(true, result.transactionId);
      } else {
        setPaymentStatus('failed');
        toast.error('Payment failed', {
          description: result.error || 'Could not process payment'
        });
        
        if (onPaymentProcessed) onPaymentProcessed(false);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentStatus('failed');
      toast.error('Payment error', {
        description: 'Could not process payment at this time'
      });
      
      if (onPaymentProcessed) onPaymentProcessed(false);
    }
  };

  return (
    <div className="space-y-5">
      <AiCardVerification 
        isVerifying={aiVerificationStatus === 'verifying'}
        isVerified={aiVerificationStatus === 'verified'}
        verificationScore={aiScore || undefined}
        riskLevel={riskLevel || undefined}
        stripeVerified={stripeReady}
        onVerificationComplete={(success) => {
          if (onAiVerified) onAiVerified(success);
        }}
      />
      
      <Form {...form}>
        <form className="space-y-3">
          <CardFormFields form={form} />
          <SaveCardCheckbox form={form} />
        </form>
      </Form>
      
      <CardActionButtons 
        aiVerificationStatus={aiVerificationStatus}
        paymentStatus={paymentStatus}
        stripeReady={stripeReady}
        handleVerifyCard={handleVerifyCard}
        handleProcessPayment={handleProcessPayment}
        disableProcessing={disableProcessing}
        recipientId={recipientId}
        amount={amount}
      />
    </div>
  );
};

export default CardPaymentForm;
